/**
 * Website Crawler Service
 * Analyzes entire website structure via sitemap and key pages
 */

import { openaiService } from './openaiService';

export interface WebsitePage {
  url: string;
  title: string;
  type: 'homepage' | 'product' | 'checkout' | 'contact' | 'about' | 'other';
  hasConversions: boolean;
  trackingCodes: string[];
}

export interface WebsiteCrawlResult {
  mainUrl: string;
  pages: WebsitePage[];
  sitemap: string[];
  conversionPages: string[];
  productPages: string[];
  totalPages: number;
  crawledAt: Date;
}

export const websiteCrawler = {
  /**
   * Crawl entire website starting from URL
   */
  async crawlWebsite(url: string): Promise<WebsiteCrawlResult> {
    console.log('🕷️ Crawling website:', url);
    
    try {
      // 1. Try to fetch sitemap.xml
      const sitemap = await this.fetchSitemap(url);
      
      // 2. Identify important pages
      const importantPages = this.findImportantPages(sitemap, url);
      
      // 3. Limit to top 10 pages to avoid overwhelming OpenAI
      const pagesToAnalyze = importantPages.slice(0, 10);
      
      console.log(`📄 Found ${sitemap.length} pages in sitemap, analyzing top ${pagesToAnalyze.length}`);
      
      // 4. Analyze pages in parallel (but limit concurrency)
      const analyses = await this.analyzePagesInBatches(pagesToAnalyze, 3);
      
      return {
        mainUrl: url,
        pages: analyses,
        sitemap: sitemap,
        conversionPages: analyses.filter(p => p.hasConversions).map(p => p.url),
        productPages: analyses.filter(p => p.type === 'product').map(p => p.url),
        totalPages: sitemap.length,
        crawledAt: new Date()
      };
    } catch (error) {
      console.error('Error crawling website:', error);
      
      // Fallback: Just analyze the main URL
      return {
        mainUrl: url,
        pages: [{
          url: url,
          title: new URL(url).hostname,
          type: 'homepage',
          hasConversions: false,
          trackingCodes: []
        }],
        sitemap: [url],
        conversionPages: [],
        productPages: [],
        totalPages: 1,
        crawledAt: new Date()
      };
    }
  },

  /**
   * Fetch and parse sitemap.xml
   */
  async fetchSitemap(baseUrl: string): Promise<string[]> {
    const sitemapUrls = [
      `${baseUrl}/sitemap.xml`,
      `${baseUrl}/sitemap_index.xml`,
      `${new URL(baseUrl).origin}/sitemap.xml`
    ];

    for (const sitemapUrl of sitemapUrls) {
      try {
        // Use OpenAI to fetch (no CORS issues)
        const prompt = `Fetch the sitemap from ${sitemapUrl} and extract all <loc> URLs. Return as JSON array of URLs.`;
        const response = await openaiService.generalQuery(prompt, 'You are a web scraping assistant.');
        
        // Parse response
        const urls = JSON.parse(response.replace(/```json\n?/g, '').replace(/```/g, '').trim());
        
        if (Array.isArray(urls) && urls.length > 0) {
          console.log(`✅ Found ${urls.length} URLs in sitemap`);
          return urls;
        }
      } catch (error) {
        console.warn(`Failed to fetch sitemap from ${sitemapUrl}`);
      }
    }

    // Fallback: Return just the base URL
    console.warn('⚠️ No sitemap found, using base URL only');
    return [baseUrl];
  },

  /**
   * Find important pages (conversion-likely pages)
   */
  findImportantPages(sitemap: string[], baseUrl: string): string[] {
    const important: string[] = [baseUrl]; // Always include homepage
    
    const keywords = {
      conversion: ['checkout', 'cart', 'contact', 'buy', 'purchase', 'order', 'pricing', 'plans'],
      product: ['product', 'shop', 'store', 'item', 'catalog'],
      high: ['about', 'services', 'solutions']
    };

    // Prioritize conversion pages
    const conversionPages = sitemap.filter(url => 
      keywords.conversion.some(kw => url.toLowerCase().includes(kw))
    );
    
    // Then product pages
    const productPages = sitemap.filter(url => 
      keywords.product.some(kw => url.toLowerCase().includes(kw))
    );
    
    // Then other important pages
    const otherPages = sitemap.filter(url => 
      keywords.high.some(kw => url.toLowerCase().includes(kw))
    );

    return [
      ...important,
      ...conversionPages.slice(0, 5),
      ...productPages.slice(0, 3),
      ...otherPages.slice(0, 2)
    ].filter((url, index, self) => self.indexOf(url) === index); // Unique
  },

  /**
   * Analyze pages in batches to avoid overwhelming API
   */
  async analyzePagesInBatches(
    urls: string[],
    batchSize: number
  ): Promise<WebsitePage[]> {
    const results: WebsitePage[] = [];
    
    for (let i = 0; i < urls.length; i += batchSize) {
      const batch = urls.slice(i, i + batchSize);
      const batchResults = await Promise.all(
        batch.map(url => this.analyzePage(url))
      );
      results.push(...batchResults);
      
      // Small delay between batches
      if (i + batchSize < urls.length) {
        await new Promise(resolve => setTimeout(resolve, 1000));
      }
    }
    
    return results;
  },

  /**
   * Analyze a single page
   */
  async analyzePage(url: string): Promise<WebsitePage> {
    try {
      const prompt = `Analyze this URL and determine:
1. Page type (homepage, product, checkout, contact, about, other)
2. Does it have conversion points? (forms, buy buttons, checkout)
3. Any tracking codes visible? (GTM, GA4, etc.)

URL: ${url}

Return JSON:
{
  "title": "Page title",
  "type": "homepage|product|checkout|contact|about|other",
  "hasConversions": true,
  "trackingCodes": ["GTM-XXX", "G-XXX"]
}`;

      const response = await openaiService.generalQuery(prompt, 'You are a web analyst. Return only JSON.');
      const data = JSON.parse(response.replace(/```json\n?/g, '').replace(/```/g, '').trim());
      
      return {
        url,
        title: data.title || url,
        type: data.type || 'other',
        hasConversions: data.hasConversions || false,
        trackingCodes: data.trackingCodes || []
      };
    } catch (error) {
      console.error('Error analyzing page:', url, error);
      return {
        url,
        title: url,
        type: 'other',
        hasConversions: false,
        trackingCodes: []
      };
    }
  }
};

